import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { clientAPI, mailingListAPI } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Eye, Users, Search, ChevronLeft, ChevronRight, CheckCircle, ListPlus, X } from "lucide-react";

function ContactPicker({ open, onClose, selectedIds, onConfirm }) {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [localSelected, setLocalSelected] = useState(new Set());
  const PAGE_SIZE = 20;

  useEffect(() => { if (open) setLocalSelected(new Set(selectedIds || [])); }, [open, selectedIds]);

  const load = useCallback(() => {
    const params = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
    if (search) params.search = search;
    if (type) params.type = type;
    clientAPI.list(params).then(r => { setClients(r.data.items || r.data); setTotal(r.data.total || 0); }).catch(() => {});
  }, [search, type, page]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const getEmail = (c) => { if (c.email) return c.email; if (c.emails) for (const e of c.emails) { if (e.value) return e.value; } return ""; };
  const toggle = (id) => setLocalSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectAll = () => setLocalSelected(prev => { const n = new Set(prev); clients.forEach(c => { if (getEmail(c)) n.add(c.id); }); return n; });

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Selectionner des contacts ({localSelected.size})</DialogTitle></DialogHeader>
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input className="pl-8 h-9 text-sm" placeholder="Rechercher..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} data-testid="picker-search" />
          </div>
          <Select value={type || "all"} onValueChange={v => { setType(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous</SelectItem><SelectItem value="client">Clients</SelectItem><SelectItem value="prospect">Prospects</SelectItem></SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="text-xs h-9" onClick={selectAll}>Tout cocher</Button>
        </div>
        <div className="border rounded-lg max-h-[50vh] overflow-y-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead className="w-10"></TableHead><TableHead>Société / Nom</TableHead><TableHead>Email</TableHead><TableHead>Type</TableHead></TableRow></TableHeader>
            <TableBody>
              {clients.map(c => {
                const email = getEmail(c);
                return (
                  <TableRow key={c.id} className={`cursor-pointer hover:bg-slate-50 ${!email ? "opacity-40" : ""}`} onClick={() => email && toggle(c.id)}>
                    <TableCell><Checkbox checked={localSelected.has(c.id)} disabled={!email} /></TableCell>
                    <TableCell className="text-sm font-medium">{c.company || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{email || <span className="text-red-400 italic">Pas d'email</span>}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{c.type === "client" ? "Client" : "Prospect"}</Badge></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-slate-500">{total} contact(s)</span>
          <div className="flex gap-1">
            <Button variant="outline" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></Button>
            <span className="text-xs text-slate-500 px-2 leading-7">Page {page}/{Math.ceil(total / PAGE_SIZE) || 1}</span>
            <Button variant="outline" size="icon" className="h-7 w-7" disabled={page >= Math.ceil(total / PAGE_SIZE)} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></Button>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => setLocalSelected(new Set())}>Tout decocher</Button>
          <Button onClick={() => { onConfirm(Array.from(localSelected)); onClose(); }} data-testid="picker-confirm"><CheckCircle size={14} className="mr-1.5" />Confirmer ({localSelected.size})</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MailingListsPage() {
  const [lists, setLists] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editList, setEditList] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [showPicker, setShowPicker] = useState(false);
  const [pickerTarget, setPickerTarget] = useState(null);

  const load = useCallback(() => { mailingListAPI.list().then(r => setLists(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name) { toast.error("Nom requis"); return; }
    try { await mailingListAPI.create(form); toast.success("Liste créée"); setShowCreate(false); load(); } catch { toast.error("Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette liste ?")) return;
    try { await mailingListAPI.delete(id); toast.success("Supprimee"); load(); } catch { toast.error("Erreur"); }
  };

  const openDetail = async (id) => {
    try { const r = await mailingListAPI.get(id); setEditList(r.data); } catch { toast.error("Erreur"); }
  };

  const removeContact = async (contactId) => {
    if (!editList) return;
    try {
      await mailingListAPI.removeContacts(editList.id, [contactId]);
      const r = await mailingListAPI.get(editList.id);
      setEditList(r.data);
      load();
      toast.success("Contact retire");
    } catch { toast.error("Erreur"); }
  };

  const getEmail = (c) => { if (c.email) return c.email; if (c.emails) for (const e of c.emails) { if (e.value) return e.value; } return ""; };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="lists-title">Listes de diffusion</h1>
          <p className="text-sm text-slate-500 mt-1">{lists.length} liste(s)</p>
        </div>
        <Button size="sm" onClick={() => { setForm({ name: "", description: "" }); setShowCreate(true); }} data-testid="new-list-btn"><Plus size={14} className="mr-1.5" />Nouvelle liste</Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50"><TableHead>Nom</TableHead><TableHead>Description</TableHead><TableHead className="text-center">Contacts</TableHead><TableHead className="text-center">Modifie</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {lists.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Aucune liste de diffusion</TableCell></TableRow>}
            {lists.map(l => (
              <TableRow key={l.id} data-testid={`list-row-${l.id}`}>
                <TableCell className="font-medium text-sm">{l.name}</TableCell>
                <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">{l.description || "-"}</TableCell>
                <TableCell className="text-center"><Badge variant="outline" className="text-xs">{(l.contact_ids || []).length}</Badge></TableCell>
                <TableCell className="text-center text-xs text-slate-500">{l.updated_at ? new Date(l.updated_at).toLocaleDateString("fr-FR") : "-"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDetail(l.id)} data-testid={`view-list-${l.id}`}><Eye size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-indigo-600" onClick={() => { setPickerTarget(l.id); setShowPicker(true); }} title="Ajouter des contacts" data-testid={`add-to-list-${l.id}`}><ListPlus size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(l.id)} data-testid={`delete-list-${l.id}`}><Trash2 size={13} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create list dialog */}
      <Dialog open={showCreate} onOpenChange={v => { if (!v) setShowCreate(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nouvelle liste de diffusion</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Clients abonnement Premium" data-testid="list-name" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description optionnelle..." data-testid="list-desc" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button><Button onClick={handleCreate} data-testid="list-save-btn">Créer</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail list dialog */}
      <Dialog open={!!editList} onOpenChange={v => { if (!v) setEditList(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {editList && (<>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">{editList.name} <Badge variant="outline" className="text-xs font-normal">{(editList.contacts || []).length} contact(s)</Badge></DialogTitle>
            </DialogHeader>
            {editList.description && <p className="text-sm text-slate-500">{editList.description}</p>}
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={() => { setPickerTarget(editList.id); setShowPicker(true); }} data-testid="list-add-contacts-btn"><ListPlus size={14} className="mr-1.5" />Ajouter des contacts</Button>
            </div>
            <div className="border rounded-lg max-h-[50vh] overflow-y-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Société / Nom</TableHead><TableHead>Email</TableHead><TableHead>Type</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
                <TableBody>
                  {(editList.contacts || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">Aucun contact dans cette liste</TableCell></TableRow>}
                  {(editList.contacts || []).map(c => (
                    <TableRow key={c.id}>
                      <TableCell className="text-sm font-medium">{c.company || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "-"}</TableCell>
                      <TableCell className="text-xs text-slate-500">{getEmail(c) || "-"}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{c.type === "client" ? "Client" : "Prospect"}</Badge></TableCell>
                      <TableCell><Button variant="ghost" size="icon" className="h-6 w-6 text-red-400 hover:text-red-600" onClick={() => removeContact(c.id)}><X size={12} /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Contact picker */}
      <ContactPicker
        open={showPicker}
        onClose={() => setShowPicker(false)}
        selectedIds={editList?.contacts?.map(c => c.id) || []}
        onConfirm={async (ids) => {
          if (!pickerTarget) return;
          try {
            await mailingListAPI.addContacts(pickerTarget, ids);
            toast.success("Contacts ajoutés");
            load();
            if (editList?.id === pickerTarget) {
              const r = await mailingListAPI.get(pickerTarget);
              setEditList(r.data);
            }
          } catch { toast.error("Erreur"); }
        }}
      />
    </div>
  );
}
